import React, { useState, useEffect, useCallback } from 'react';
import { subscriptionApi } from '../services/subscriptionApi';
import { artistApi } from '../services/artistApi';
import { initializeRevenueCat, isRevenueCatInitialized, checkPendingTransfer } from '../config/revenuecat';
import { useAuth } from '../contexts/AuthContext';
import type { CustomerInfo, Package, Offerings } from '@revenuecat/purchases-js';
import './SubscriptionManager.css';

interface SubscriptionManagerProps {
  onClose?: () => void;
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [offerings, setOfferings] = useState<Offerings | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rcInitialized, setRcInitialized] = useState(false);

  const ensureRevenueCatInitialized = useCallback(() => {
    // Check if we just completed a page reload for user transfer
    const pendingUserId = checkPendingTransfer();
    const targetUserId = pendingUserId || user?.id;
    
    if (!isRevenueCatInitialized() && targetUserId && user) {
      console.log('RevenueCat not initialized, attempting to initialize with user ID:', targetUserId);
      if (pendingUserId) {
        console.log('✓ Completing user ID transfer after page reload');
      }
      try {
        // Initialize RevenueCat with the authenticated user's ID and email
        // This user ID will be sent to RevenueCat as the appUserId
        initializeRevenueCat(targetUserId, user.email);
        setRcInitialized(true);
        console.log('✓ RevenueCat initialized successfully with App User ID:', targetUserId);
        return true;
      } catch (error) {
        console.error('Failed to initialize RevenueCat:', error);
        setError('Failed to initialize payment system. Please refresh the page and try again.');
        return false;
      }
    }
    setRcInitialized(isRevenueCatInitialized());
    return isRevenueCatInitialized();
  }, [user]);

  const loadSubscriptionData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Ensure RevenueCat is initialized
      if (!ensureRevenueCatInitialized()) {
        setError('Payment system not initialized. Please log out and log back in.');
        return;
      }

      console.log('Loading subscription data...');

      // Check if user has anonymous ID and needs transfer
      let info = await subscriptionApi.getCustomerInfo();
      if (info && user?.id && info.originalAppUserId.startsWith('$RCAnonymousID:')) {
        console.warn('⚠️ User has anonymous RevenueCat ID:', info.originalAppUserId);
        console.warn('✓ Re-configuring RC with real user ID:', user.id);
        await subscriptionApi.transferPurchasesToUser(user.id, user.email);
        // Re-fetch after the user switch (no page reload needed)
        info = await subscriptionApi.getCustomerInfo();
        console.log('✓ After transfer, RC user ID:', info?.originalAppUserId);
      }

      // Check subscription status
      const isActive = await subscriptionApi.hasActiveSubscription();
      setHasSubscription(isActive);

      // Use the customer info we already fetched
      setCustomerInfo(info);
      
      console.log('Subscription status:', { isActive, customerInfo: info });

      // If no subscription, load offerings
      if (!isActive) {
        const availableOfferings = await subscriptionApi.getOfferings();
        console.log('Available offerings:', availableOfferings);
        setOfferings(availableOfferings);

        // Find the MonthlyPremium package
        if (availableOfferings?.current) {
          console.log('Current offering packages:', availableOfferings.current.availablePackages);
          const monthlyPackage = availableOfferings.current.availablePackages.find(
            (pkg: Package) => pkg.identifier === 'MonthlyPremium' || pkg.webBillingProduct.identifier === 'MonthlyPremium'
          );
          if (monthlyPackage) {
            console.log('Found MonthlyPremium package:', monthlyPackage);
            setSelectedPackage(monthlyPackage);
          } else if (availableOfferings.current.availablePackages.length > 0) {
            // Fallback to first available package
            console.log('Using first available package:', availableOfferings.current.availablePackages[0]);
            setSelectedPackage(availableOfferings.current.availablePackages[0]);
          } else {
            console.warn('No packages available in current offering');
          }
        } else {
          console.warn('No current offering available');
        }
      }
    } catch (err) {
      console.error('Failed to load subscription data:', err);
      setError(`Failed to load subscription information. ${err instanceof Error ? err.message : 'Please try again later.'}`);
    } finally {
      setLoading(false);
    }
  }, [ensureRevenueCatInitialized, user?.id]);

  useEffect(() => {
    loadSubscriptionData();
  }, [loadSubscriptionData]);

  const handlePurchase = async () => {
    if (!selectedPackage) {
      setError('No package selected');
      return;
    }

    // Ensure RevenueCat is initialized before purchase
    if (!ensureRevenueCatInitialized()) {
      setError('Payment system not initialized. Please try again.');
      return;
    }

    if (!user?.id) {
      setError('User not authenticated. Please log in again.');
      return;
    }

    try {
      setPurchasing(true);
      setError(null);

      console.log('=== Initiating Purchase for User ===');
      console.log('User ID:', user.id);
      console.log('User Email:', user.email);
      console.log('Package:', selectedPackage.identifier);

      await subscriptionApi.purchasePackage(selectedPackage);
      
      console.log('Purchase completed successfully');
      
      // Refresh entitlements for all user's artists
      try {
        const artists = await artistApi.getArtistsByUserId(user.id);
        await Promise.all(
          artists.map(artist => 
            artist.id ? artistApi.refreshEntitlements(artist.id) : Promise.resolve()
          )
        );
        console.log('Artist entitlements refreshed successfully');
      } catch (entitlementErr) {
        console.error('Failed to refresh entitlements:', entitlementErr);
        // Don't fail the purchase if entitlement refresh fails
      }
      
      // Reload subscription data
      await loadSubscriptionData();
    } catch (err: any) {
      console.error('Purchase failed:', err);
      if (err?.message?.includes('user_cancelled')) {
        setError('Purchase cancelled');
      } else {
        setError(`Purchase failed: ${err?.message || 'Please try again.'}`);
      }
    } finally {
      setPurchasing(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setError(null);
      console.log('Attempting to get management URL...');
      
      const managementUrl = await subscriptionApi.getManagementUrl();
      
      if (managementUrl) {
        console.log('Opening management URL:', managementUrl);
        window.open(managementUrl, '_blank');
      } else {
        console.error('No management URL available');
        setError(
          'Management portal is not yet available. ' +
          'This can take a few moments after subscription. ' +
          'Please try again in a minute, or contact support if the issue persists.'
        );
        
        // Provide some helpful context
        if (customerInfo) {
          console.log('Current subscription info:', {
            activeSubscriptions: Array.from(customerInfo.activeSubscriptions),
            hasActiveEntitlements: Object.keys(customerInfo.entitlements.active).length > 0,
            originalAppUserId: customerInfo.originalAppUserId
          });
        }
      }
    } catch (err) {
      console.error('Failed to get management URL:', err);
      setError(
        'Unable to access subscription management. ' +
        'Please contact support at support@yourdomain.com with your user ID: ' + 
        (user?.id || 'unknown')
      );
    }
  };

  if (loading) {
    return (
      <div className="subscription-manager">
        <div className="subscription-container">
          <div className="subscription-loading">
            <div className="spinner"></div>
            <p>Loading subscription information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-manager">
      <div className="subscription-container">
        <div className="subscription-header">
          <h2>Subscription Management</h2>
          {onClose && (
            <button className="close-button" onClick={onClose} aria-label="Close">
              ×
            </button>
          )}
        </div>
        
        {/* Debug info bar */}
        <div className={`debug-bar ${rcInitialized ? 'initialized' : 'not-initialized'}`}>
          <div className="debug-info-group">
            <span className="debug-status">
              {rcInitialized ? '✓ Payment system ready' : '⚠ Payment system initializing...'}
            </span>
            {user && (
              <span className="debug-user-info">
                <strong>User:</strong> {user.email} <span className="user-id-badge">ID: {user.id}</span>
              </span>
            )}
          </div>
          <button className="debug-details-btn" onClick={() => console.log('Current user:', user)}>
            View Details
          </button>
        </div>

        {error && (
          <div className="subscription-error">
            <div>
              <p><strong>Error:</strong> {error}</p>
              <p className="error-hint">
                <strong>Troubleshooting steps:</strong>
                <br />1. Check the browser console (F12) for detailed error messages
                <br />2. Ensure you are logged in
                <br />3. Verify RevenueCat API key is set in .env file
                <br />4. Check that offerings are configured in RevenueCat dashboard
                <br />5. Try refreshing the page
              </p>
            </div>
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        <div className="subscription-content">
          {hasSubscription ? (
            // User has active subscription - show management options
            <div className="subscription-active">
              <div className="subscription-status">
                <svg className="check-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3>Premium Active</h3>
                <p>You have an active premium subscription</p>
              </div>

              {customerInfo && (
                <div className="subscription-details">
                  <h4>Subscription Details</h4>
                  <div className="subscription-info">
                    {Object.entries(customerInfo.entitlements.active).map(([key, entitlement]) => (
                      <div key={key} className="entitlement-item">
                        <div className="entitlement-header">
                          <strong>{entitlement.productIdentifier}</strong>
                        </div>
                        <div className="entitlement-meta">
                          {entitlement.expirationDate && (
                            <p>
                              Renews: {new Date(entitlement.expirationDate).toLocaleDateString()}
                            </p>
                          )}
                          {entitlement.store && (
                            <p className="store-badge">{entitlement.store}</p>
                          )}
                          {entitlement.willRenew !== undefined && (
                            <p className={entitlement.willRenew ? 'renew-active' : 'renew-cancelled'}>
                              {entitlement.willRenew ? 'Auto-renewing' : 'Cancelled (active until expiry)'}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="subscription-actions">
                <button 
                  className="manage-button"
                  onClick={handleManageSubscription}
                >
                  Manage Subscription
                </button>
                <button 
                  className="refresh-button"
                  onClick={() => {
                    setError(null);
                    loadSubscriptionData();
                  }}
                  title="Refresh subscription data"
                >
                  🔄 Refresh
                </button>
                <p className="manage-hint">
                  Manage your billing, change plans, or cancel your subscription.
                  <br />
                  <small>If the management portal doesn't open, please wait a minute and try again.</small>
                </p>
              </div>
            </div>
          ) : (
            // No subscription - show paywall
            <div className="subscription-paywall">
              <div className="paywall-header">
                <h3>Upgrade to Premium</h3>
                <p>Unlock unlimited feedback and advanced features</p>
              </div>

              <div className="paywall-features">
                <div className="feature-item">
                  <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Support The Project</span>
                </div>
                <div className="feature-item">
                  <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Unlimited Track Uploads</span>
                </div>
                <div className="feature-item">
                  <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Ad-Free Experience</span>
                </div>
                <div className="feature-item">
                  <svg className="feature-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>1.5X Karma Power</span>
                </div>
              </div>

              {selectedPackage && (
                <div className="paywall-package">
                  <div className="package-details">
                    <h4>{selectedPackage.webBillingProduct.title || 'Premium Subscription'}</h4>
                    <p className="package-description">
                      {selectedPackage.webBillingProduct.description || 'Upgrade to premium for unlimited access'}
                    </p>
                    <div className="package-price">
                      <span className="price-amount">
                        {selectedPackage.webBillingProduct.price.formattedPrice}
                      </span>
                      <span className="price-period">
                        /{selectedPackage.webBillingProduct.period?.unit || 'month'}
                      </span>
                    </div>
                  </div>

                  <button 
                    className="subscribe-button"
                    onClick={handlePurchase}
                    disabled={purchasing}
                  >
                    {purchasing ? 'Processing...' : 'Subscribe Now'}
                  </button>
                  
                  <p className="purchase-note">
                    You'll be redirected to complete your purchase securely.
                    {user && (
                      <span className="subscription-user-note">
                        <br />Subscribing as: <strong>{user.email}</strong>
                      </span>
                    )}
                  </p>
                </div>
              )}

              {!selectedPackage && offerings && (
                <div className="no-packages">
                  <p>No subscription packages available at this time.</p>
                  <p className="debug-info">Check console for details.</p>
                  <button onClick={loadSubscriptionData}>Retry</button>
                </div>
              )}
              
              {!selectedPackage && !offerings && (
                <div className="no-packages">
                  <p>Unable to load subscription offerings.</p>
                  <p className="debug-info">Please ensure you are logged in and try again.</p>
                  <button onClick={loadSubscriptionData}>Retry</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManager;
